import type { AnalyticsAction } from "@/enums/AnalyticsAction";
import type { AnalyticsCategory } from "@/enums/AnalyticsCategory";
import { TrackingEnvironment } from "@/enums/TrackingEnvironment";

type PaqEntry = [string, ...unknown[]];

declare global {
  /** Matomo's command queue, created by the snippet in `index.html`. Absent under test. */
  var _paq: PaqEntry[] | undefined;
}

/** The visit-scoped Environment custom dimension on Matomo site 15. */
const ENVIRONMENT_DIMENSION = 1;

/**
 * A production build served from this machine, through `vite preview` or a puppet run,
 * still counts as local, so the hostname is checked before the build mode.
 */
function detectEnvironment(): TrackingEnvironment {
  if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
    return TrackingEnvironment.Localhost;
  }

  return import.meta.env.MODE === "dev"
    ? TrackingEnvironment.DeployedDev
    : TrackingEnvironment.Prod;
}

/**
 * Tags the visit with its environment and records the page view. The snippet in
 * `index.html` holds the page view back so that no hit leaves without the tag.
 */
export function startTracking(): void {
  globalThis._paq?.push(
    ["setCustomDimension", ENVIRONMENT_DIMENSION, detectEnvironment()],
    ["trackPageView"],
  );
}

/** Matomo reads the event's fields by position, so a value is only sent with a name. */
export function trackEvent(
  category: AnalyticsCategory,
  action: AnalyticsAction,
  name?: string,
  value?: number,
): void {
  const entry: PaqEntry = ["trackEvent", category, action];

  if (name !== undefined) {
    entry.push(name);
  }

  if (name !== undefined && value !== undefined) {
    entry.push(value);
  }

  globalThis._paq?.push(entry);
}
